import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { signInWithCustomToken } from "firebase/auth";
import { Loader2, Smartphone } from "lucide-react";
import { auth } from "../config/firebase";
import api from "../services/api";
import {
  BRAND,
  EDITORIAL_FONT,
  PatternTile,
  EditorialShell,
  EditorialCard,
  SerifHeading,
  PillButton,
  useEditorialPalette,
} from "../components/editorial/EditorialPrimitives";

export default function ContinueSignup() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const palette = useEditorialPalette();

  const [status, setStatus] = useState("validating"); // validating, valid, invalid, error
  const [error, setError] = useState("");

  const token = searchParams.get("token");
  const role = searchParams.get("role");
  const fromMobile = searchParams.get("from") === "mobile";
  const dest = searchParams.get("dest");

  useEffect(() => {
    const validateAndSignIn = async () => {
      if (!token) {
        setStatus("invalid");
        setError("Missing login token. Please try again from the app.");
        return;
      }

      try {
        const response = await api.post("/v1/auth/validate-login-link", { token });
        const data = response.data;

        if (!data.valid) {
          setStatus("invalid");
          setError(data.error || "Invalid or expired login link. Please try again.");
          return;
        }

        setStatus("valid");
        await signInWithCustomToken(auth, data.customToken);

        await new Promise((resolve) => setTimeout(resolve, 500));

        const targetRole = data.role || role;
        if (targetRole === "masjid") {
          navigate("/masjid-signup", { state: { fromMobile: true }, replace: true });
        } else if (targetRole === "halal") {
          if (dest === "billing") {
            navigate("/halal", { state: { fromMobile: true }, replace: true });
          } else {
            navigate("/halal-signup", { state: { fromMobile: true }, replace: true });
          }
        } else {
          navigate("/", { replace: true });
        }
      } catch (err) {
        console.error("Validation error:", err);
        setStatus("error");

        if (err.response?.status === 400) {
          setError(err.response.data?.error || "Invalid or expired login link.");
        } else if (err.response?.status === 429) {
          setError("Too many attempts. Please wait a moment and try again.");
        } else {
          setError("Unable to verify login. Please try again from the app.");
        }
      }
    };

    validateAndSignIn();
  }, [token, role, navigate, dest]);

  const handleReturnToApp = () => {
    window.location.href = "muslim://signup-complete";
  };

  return (
    <EditorialShell className="flex items-center justify-center">
      <PatternTile opacity={0.05} />

      <button
        onClick={() => navigate("/")}
        className="absolute left-5 top-5 z-10 flex items-center gap-2.5 transition-opacity hover:opacity-80 sm:left-7 sm:top-7"
      >
        <span
          style={{
            fontFamily: EDITORIAL_FONT.serif,
            fontSize: 24,
            fontWeight: 600,
            letterSpacing: "-0.02em",
            color: palette.ink,
          }}
        >
          Muslim<span style={{ color: BRAND.gold }}>+</span>
        </span>
      </button>

      <div className="container relative flex min-h-screen items-center justify-center px-4 py-10">
        <EditorialCard
          variant="ivory"
          className="w-full max-w-md p-8 text-center sm:p-10"
          style={{ animation: "editorialFadeUp 0.5s ease-out both" }}
        >
          <SerifHeading
            level={1}
            className="mb-3"
            style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.25rem)", lineHeight: 1 }}
          >
            {status === "validating" && (
              <>
                Verifying<em style={{ color: BRAND.gold, fontWeight: 400 }}>…</em>
              </>
            )}
            {status === "valid" && (
              <>
                <em style={{ color: BRAND.gold, fontWeight: 400 }}>Welcome.</em>
              </>
            )}
            {(status === "invalid" || status === "error") && (
              <>
                Unable to <em style={{ color: BRAND.gold, fontWeight: 400 }}>continue.</em>
              </>
            )}
          </SerifHeading>

          <p className="mb-8 text-sm leading-relaxed" style={{ color: palette.muted }}>
            {status === "validating" && "Please wait while we verify your login."}
            {status === "valid" && "Redirecting you to complete signup…"}
            {(status === "invalid" || status === "error") && "There was a problem with your login link."}
          </p>

          {status === "validating" && (
            <div className="flex flex-col items-center">
              <Loader2 className="mb-4 h-14 w-14 animate-spin" style={{ color: BRAND.gold }} />
              <p className="text-sm" style={{ color: palette.muted }}>
                Connecting to your account…
              </p>
            </div>
          )}

          {status === "valid" && (
            <div className="flex flex-col items-center">
              <Loader2 className="h-6 w-6 animate-spin" style={{ color: BRAND.gold }} />
            </div>
          )}

          {(status === "invalid" || status === "error") && (
            <div className="flex flex-col items-center">
              <div
                className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl"
                style={{ background: BRAND.terracotta + "18" }}
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={BRAND.terracotta} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <circle cx="12" cy="12" r="10" />
                  <path d="M15 9l-6 6M9 9l6 6" />
                </svg>
              </div>
              <p className="mb-6 max-w-xs text-sm" style={{ color: palette.muted }}>
                {error}
              </p>

              <div className="flex flex-col gap-3 w-full">
                {fromMobile && (
                  <PillButton
                    variant="primary"
                    onClick={handleReturnToApp}
                    className="w-full justify-center"
                    size="lg"
                  >
                    <Smartphone className="h-4 w-4" />
                    Return to app
                  </PillButton>
                )}
                <PillButton
                  variant="outline"
                  onClick={() => navigate("/login")}
                  className="w-full justify-center"
                >
                  Go to sign in
                </PillButton>
              </div>
            </div>
          )}
        </EditorialCard>
      </div>

      <style>{`
        @keyframes editorialFadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </EditorialShell>
  );
}
